/**
 * OrderConfirmation.tsx — the order-received screen with the 3 next steps.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: checkout ends here. Copy follows the payment capture route from server
 * plan-preview (Route 1 hold vs Route 2 immediate) so patients are not told
 * "awaiting clinician approval" when payment was already authorised.
 *
 * THE CELEBRATED ARRIVAL (CTO 2026-09-24): on a JUST-completed order
 * (`celebrate`, set by ThankYouPage from `justCompleted`) the ✓ circle gives way
 * to the celebration illustration and the warm line joins the title — the same
 * moment the guest's account-step modal marks. Everything route-aware below is
 * unchanged, and a re-shown confirmation (a refresh-resume onto an order that
 * finished earlier) keeps the plain ✓: it is not a new moment.
 */
import { ZoneExitLink } from '../tracking/ZoneExitLink';
import { Alert, ButtonArrow, buttonClass, buttonStyle, Card, CheckIcon, Help, Title } from '../ui/kit';
import { usePrefersReducedMotion } from '../ui/motion';
import {
  confirmationHelpText,
  orderConfirmationCopy,
  type PaymentCaptureRoute,
} from './paymentCopy';
import { KeepCompoundsWhole } from '../ui/KeepCompoundsWhole';
import { CelebrationArt } from './Celebration';
import { CELEBRATION_LEAD } from './celebrationCopy';

export function OrderConfirmation({
  medicationName,
  captureRoute = 'hold',
  celebrate = false,
}: {
  medicationName?: string;
  /** From plan-preview `hasPlan` — never from query params. */
  captureRoute?: PaymentCaptureRoute;
  /** A just-completed order: the illustration + warm line instead of the plain ✓. */
  celebrate?: boolean;
}) {
  const reduced = usePrefersReducedMotion();
  const copy = orderConfirmationCopy(captureRoute);
  return (
    // The journey's classic column (32rem; 40rem at >=880px — CTO 2026-09-24,
    // `--gps-journey-col` on the thank-you page's `gps-journey-root` frame), so
    // the confirmation is as wide as the card that may follow it.
    <div style={{ maxWidth: 'var(--gps-journey-col, 32rem)', margin: '0 auto' }}>
      <div style={{ textAlign: 'center' }}>
        {celebrate ? (
          <CelebrationArt
            style={{
              display: 'block',
              width: '200px',
              maxWidth: '70%',
              height: 'auto',
              margin: '0 auto 10px',
              // Same arrival beat the ✓ circle had; reduced motion → static.
              animation: reduced ? undefined : 'gps-check-in 480ms var(--gps-ease) both',
            }}
          />
        ) : (
          <div
            aria-hidden="true"
            style={{
              width: '64px',
              height: '64px',
              borderRadius: 'var(--gps-r-pill)',
              background: 'var(--gps-primary-light)',
              color: 'var(--gps-green-text)',
              display: 'grid',
              placeItems: 'center',
              fontSize: '30px',
              margin: '6px auto 14px',
              // The "you converted" beat: the ✓ circle scales + fades in once on
              // arrival. Reduced-motion → static (E3 #12).
              animation: reduced ? undefined : 'gps-check-in 420ms var(--gps-ease) both',
            }}
          >
            <CheckIcon size={30} />
          </div>
        )}
        <Title>Order received</Title>
        {celebrate ? (
          // The modal's warm line, kept whole the way the modal keeps it: at 360px
          // and 375px it broke as "weight-" / "loss journey." here, one screen
          // after the modal was fixed (review, 2026-09-24). `hyphens: manual` so
          // the browser never adds a hyphen of its own either.
          <p
            style={{
              margin: '0 0 6px',
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: 1.45,
              color: 'var(--gps-green-text)',
              WebkitHyphens: 'manual',
              hyphens: 'manual',
            }}
          >
            <KeepCompoundsWhole text={CELEBRATION_LEAD} />
          </p>
        ) : null}
        <Help>{confirmationHelpText(captureRoute, medicationName)}</Help>
      </div>

      <Alert tone="success" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
        {copy.alert}
      </Alert>

      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--gps-s-3)' }}>
        {copy.steps.map((step, i) => (
          <li key={step.title}>
            <Card>
              <div style={{ display: 'flex', gap: 'var(--gps-s-3)', alignItems: 'flex-start' }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: '28px',
                    height: '28px',
                    flex: 'none',
                    borderRadius: 'var(--gps-r-pill)',
                    background: 'var(--gps-primary)',
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 700,
                    fontSize: '14px',
                  }}
                >
                  {i + 1}
                </span>
                <div>
                  <h2 style={{ fontFamily: 'var(--gps-font-body)', fontSize: '15px', fontWeight: 700, margin: '2px 0 4px', color: 'var(--gps-text)' }}>
                    {step.title}
                  </h2>
                  <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--gps-muted)' }}>{step.body}</p>
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ol>

      <div style={{ marginTop: 'var(--gps-s-5)' }}>
        {/* ZoneExitLink (full document nav) — leaving the tracked journey for the
            untracked patient portal, so the GTM container never rides into /patients. */}
        <ZoneExitLink to="/patients" className={buttonClass('cta')} style={buttonStyle('cta')}>
          Go to my dashboard
          <ButtonArrow />
        </ZoneExitLink>
      </div>
    </div>
  );
}
